/* eslint-disable @typescript-eslint/naming-convention */
import classNames from 'clsx';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameHeader, CasinoGameList, useWidgetAnchor } from '@swf/casino';
import { CacheControl, Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import { Header } from '@swf/core/enums';
import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle, isVisibleByAuth } from '@swf/core/utils/casino';

import styles from './CasinoTopGamesWidget.scss';
import { topGamesWidgetModel, useTopGamesWidgetModel } from './model';

const CasinoTopGamesWidget: WidgetFC = () => {
  const { widgetSelect, widgetConfig } = useTopGamesWidgetModel();
  const widgetState = useSelector(widgetSelect.get);
  const icon = useSelector((state: RootState) => widgetSelect.getIcon(state, widgetConfig));
  const title = useSelector((state: RootState) => widgetSelect.getTitle(state, widgetConfig));
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const sliderRef = React.createRef<SliderHandles>();
  const containerRef = React.createRef<HTMLDivElement>();
  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });
  const {
    layout,
    sub_title: subTitle,
    min_games: minGames,
    hide_on_login: hideOnLogin,
    logged_in_only: loggedInOnly,
    more_link: moreLink
  } = widgetConfig;
  const { games, loading } = widgetState;
  const shouldRender =
    games.length >= minGames && isVisibleByAuth(loggedInOnly, hideOnLogin, isAuthenticated);
  const widgetTargetId = useWidgetAnchor();

  return shouldRender ? (
    <section
      className={classNames(styles.container, styles[`${icon}Container` as keyof typeof styles])}
      style={getStyle(layout)}
      ref={containerRef}
      id={widgetTargetId}
    >
      <CacheControl>public, max-age=600</CacheControl>
      <Vary by={[Header.Country, Header.Currency]} />
      {
        <CasinoGameHeader
          {...widgetConfig}
          moreLink={moreLink}
          title={title}
          subTitle={subTitle}
          icon={icon}
          sliderRef={sliderRef}
          sliderState={sliderState}
        />
      }
      <CasinoGameList
        {...widgetConfig}
        games={games}
        icon={icon}
        loading={loading}
        rows={1}
        onScroll={setSliderState}
        sliderRef={sliderRef}
        numbered
        params={{ include_tags: widgetConfig.params.include_tags }}
      />
    </section>
  ) : null;
};

CasinoTopGamesWidget.model = topGamesWidgetModel;

export default CasinoTopGamesWidget;
